import createHistory from 'history/createBrowserHistory';
import { getLocaleFromPathname, localizePath } from './i18nUrls';

/**
 * @description raw browser history, without locale prefixing.
 * Only for explicit cross-language navigation (language switcher,
 * TranslationBanner suggestion). Everything else must use the default export
 * below.
 */
export const rawHistory = createHistory();

/**
 * @description re-targets an absolute navigation target to the locale of the
 * current URL. On unprefixed (french) URLs, targets are left untouched; an
 * explicitly prefixed target always wins.
 */
const prefix = (to) => {
  const currentLang = getLocaleFromPathname(rawHistory.location.pathname);
  if (!currentLang) return to;

  if (typeof to === 'string') {
    if (!to.startsWith('/') || getLocaleFromPathname(to)) return to;

    return localizePath(to, currentLang);
  }

  if (to?.pathname?.startsWith('/') && !getLocaleFromPathname(to.pathname)) {
    return { ...to, pathname: localizePath(to.pathname, currentLang) };
  }

  return to;
};

/**
 * @description the app-wide history singleton. Wraps the browser history so
 * that every push/replace (imperative calls, <Link>, <Redirect>,
 * connected-react-router) transparently keeps the current locale prefix:
 * on /en/..., history.push('/results') lands on /en/results.
 */
const history = {
  get length() {
    return rawHistory.length;
  },
  get action() {
    return rawHistory.action;
  },
  get location() {
    return rawHistory.location;
  },
  createHref: (to) =>
    typeof to === 'string' ? prefix(to) : rawHistory.createHref(prefix(to)),
  push: (to, state) => rawHistory.push(prefix(to), state),
  replace: (to, state) => rawHistory.replace(prefix(to), state),
  go: (n) => rawHistory.go(n),
  goBack: () => rawHistory.goBack(),
  goForward: () => rawHistory.goForward(),
  block: (...args) => rawHistory.block(...args),
  listen: (listener) => rawHistory.listen(listener),
};

export default history;
